/**
 * Booking Request Service
 * Handles automated booking requests to accommodation providers
 * Includes email templates, state machine, and idempotency safeguards
 */

import { supabase } from './supabaseService';
import { accommodationService } from './accommodationService';
import { emailParserService } from './emailParserService';
import {
  AccommodationBookingRequest,
  BookingRequestStatus,
  AvailabilityResponse,
  AccommodationProvider,
  AccommodationCommunication
} from '../types/accommodation';
import { CustomSafariItinerary } from '../types/customSafari';

class BookingRequestService {
  /**
   * Create and send booking request to provider
   * Includes idempotency checks and state machine validation
   */
  async createBookingRequest(
    safariBookingId: string,
    clientId: string,
    providerId: string,
    tourId: string,
    location: string,
    clientDetails: {
      name: string;
      email: string;
      phone: string;
      nationality?: string;
      specialRequests?: string;
    },
    bookingDetails: {
      guestCount: number;
      arrivalDate: string;
      departureDate: string;
      nights: number;
      roomType?: string;
      mealPlan?: 'breakfast' | 'halfBoard' | 'fullBoard' | 'none';
    }
  ): Promise<AccommodationBookingRequest | null> {
    try {
      // Get provider details
      const provider = await accommodationService.getProviderById(providerId);
      if (!provider) {
        throw new Error('Provider not found');
      }

      // Generate idempotency key
      const idempotencyKey = `booking_${safariBookingId}_${providerId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Check for duplicate request
      const existing = await supabase
        .from('accommodation_booking_requests')
        .select('id')
        .eq('idempotency_key', idempotencyKey)
        .single();

      if (existing.data) {
        console.log('Duplicate booking request detected, returning existing');
        return await this.getBookingRequestById(existing.data.id);
      }

      // Calculate response deadline
      const responseDeadline = new Date();
      responseDeadline.setHours(responseDeadline.getHours() + (provider.responseTimeHours || 24));

      // Create booking request
      const bookingRequest: Omit<AccommodationBookingRequest, 'id' | 'createdAt' | 'updatedAt' | 'version'> = {
        safariBookingId,
        clientId,
        providerId,
        tourId,
        location,
        clientName: clientDetails.name,
        clientEmail: clientDetails.email,
        clientPhone: clientDetails.phone,
        clientNationality: clientDetails.nationality,
        clientSpecialRequests: clientDetails.specialRequests,
        providerContactName: 'SightTrails Safaris',
        providerContactEmail: 'bookings@sighttrails.com',
        providerContactPhone: '+254 708 560 665',
        groupName: `SightTrails Safari Group - ${location}`,
        groupSize: bookingDetails.guestCount,
        guestCount: bookingDetails.guestCount,
        checkInDate: bookingDetails.arrivalDate,
        checkOutDate: bookingDetails.departureDate,
        arrivalDate: bookingDetails.arrivalDate,
        departureDate: bookingDetails.departureDate,
        nights: bookingDetails.nights,
        roomRequirements: bookingDetails.roomType || 'standard',
        roomType: bookingDetails.roomType,
        mealPlan: bookingDetails.mealPlan,
        specialRequests: clientDetails.specialRequests,
        status: 'draft',
        priority: 'medium' as const,
        responseDeadline: responseDeadline.toISOString(),
        paymentAuthorized: false,
        paymentCaptured: false,
        idempotencyKey
      };

      // Save to database
      const created = await accommodationService.createBookingRequest(bookingRequest);
      if (!created) {
        throw new Error('Failed to create booking request');
      }

      // Send booking request to provider
      await this.sendBookingRequestToProvider(created, provider);

      // Update status to 'sent'
      await accommodationService.updateBookingRequestStatus(
        created.id,
        'sent',
        'system',
        'Booking request sent to provider'
      );

      return created;
    } catch (error) {
      console.error('Error creating booking request:', error);
      throw error;
    }
  }

  /**
   * Send booking request email to provider
   * NEVER includes client PII - all communications appear from SightTrails
   */
  private async sendBookingRequestToProvider(
    bookingRequest: AccommodationBookingRequest,
    provider: AccommodationProvider
  ): Promise<void> {
    try {
      // Generate email content (NO CLIENT PII)
      const emailContent = this.generateBookingRequestEmail(bookingRequest, provider);

      // Send email (in production, use email service like SendGrid, AWS SES, etc.)
      // For now, we'll log it and store in communications table
      console.log('Sending booking request email to:', provider.primaryEmail);
      console.log('Email content:', emailContent);

      // Store communication
      await this.logCommunication({
        bookingRequestId: bookingRequest.id,
        direction: 'outbound',
        method: 'email',
        recipient: provider.primaryEmail || provider.contactInfo?.email || '',
        sender: 'bookings@sighttrails.com',
        subject: `Accommodation Booking Request - ${bookingRequest.groupName}`,
        content: emailContent,
        status: 'sent',
        timestamp: new Date().toISOString()
      });

      // If provider has API endpoint, also send via API
      if (provider.apiEndpoint && provider.checkMethod === 'api') {
        await this.sendBookingRequestViaAPI(bookingRequest, provider);
      }
    } catch (error) {
      console.error('Error sending booking request:', error);
      throw error;
    }
  }

  /**
   * Generate booking request email template
   * NO CLIENT PII - appears as SightTrails booking
   */
  private generateBookingRequestEmail(
    bookingRequest: AccommodationBookingRequest,
    provider: AccommodationProvider
  ): string {
    const mealPlanText = bookingRequest.mealPlan
      ? bookingRequest.mealPlan === 'breakfast' ? 'Breakfast included'
        : bookingRequest.mealPlan === 'halfBoard' ? 'Half Board'
          : bookingRequest.mealPlan === 'fullBoard' ? 'Full Board'
            : 'No meals'
      : 'To be confirmed';

    return `
Dear ${provider.contactPerson || provider.name} Team,

We hope this email finds you well.

SightTrails Safaris is requesting accommodation for our safari group as follows:

GROUP DETAILS:
- Group Name: ${bookingRequest.groupName}
- Number of Guests: ${bookingRequest.guestCount}
- Arrival Date: ${new Date(bookingRequest.arrivalDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })}
- Departure Date: ${new Date(bookingRequest.departureDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })}
- Number of Nights: ${bookingRequest.nights}
- Room Type: ${bookingRequest.roomType || 'Standard'}
- Meal Plan: ${mealPlanText}
${bookingRequest.providerSpecialRequests ? `- Special Requests: ${bookingRequest.providerSpecialRequests}` : ''}

CONTACT INFORMATION:
- Contact Person: ${bookingRequest.providerContactName}
- Email: ${bookingRequest.providerContactEmail}
- Phone: ${bookingRequest.providerContactPhone}

We kindly request your availability and pricing for the above dates. Please respond at your earliest convenience, preferably within ${provider.responseTimeHours || 24} hours.

Please confirm:
1. Availability for the requested dates
2. Total price (including all applicable taxes and fees)
3. Confirmation number (if available)
4. Check-in and check-out procedures

We look forward to your prompt response.

Best regards,
SightTrails Safaris Team
${bookingRequest.providerContactEmail}
${bookingRequest.providerContactPhone}

---
This is an automated booking request. Please reply to this email with your availability confirmation.
    `.trim();
  }

  /**
   * Send booking request via API (if provider supports it)
   */
  private async sendBookingRequestViaAPI(
    bookingRequest: AccommodationBookingRequest,
    provider: AccommodationProvider
  ): Promise<void> {
    if (!provider.apiEndpoint) {
      return;
    }

    try {
      // Prepare API payload (NO CLIENT PII)
      const payload = {
        idempotencyKey: bookingRequest.idempotencyKey,
        groupName: bookingRequest.groupName,
        guestCount: bookingRequest.guestCount,
        arrivalDate: bookingRequest.arrivalDate,
        departureDate: bookingRequest.departureDate,
        nights: bookingRequest.nights,
        roomType: bookingRequest.roomType,
        mealPlan: bookingRequest.mealPlan,
        specialRequests: bookingRequest.providerSpecialRequests,
        contactEmail: bookingRequest.providerContactEmail,
        contactPhone: bookingRequest.providerContactPhone
      };

      // In production, make actual API call
      console.log('Sending API request to:', provider.apiEndpoint);
      console.log('Payload:', payload);

      // Store communication
      await this.logCommunication({
        bookingRequestId: bookingRequest.id,
        direction: 'outbound',
        method: 'api',
        recipient: provider.apiEndpoint,
        sender: 'SightTrails API',
        subject: 'Booking Request',
        content: JSON.stringify(payload),
        status: 'sent',
        timestamp: new Date().toISOString(),
        metadata: { endpoint: provider.apiEndpoint }
      });
    } catch (error) {
      console.error('Error sending API request:', error);
      throw error;
    }
  }

  /**
   * Process provider email response
   * Parses email content and updates booking request
   */
  async processProviderEmailResponse(
    bookingRequestId: string,
    emailContent: string,
    attachments?: Array<{ filename: string; content: string; mimeType: string }>
  ): Promise<AccommodationBookingRequest | null> {
    try {
      // Parse email with confidence scoring
      const parsed = await emailParserService.parseProviderResponse(emailContent, attachments);

      // If confidence is low, flag for manual review
      if (parsed.requiresManualReview || parsed.confidence < 0.85) {
        await accommodationService.updateBookingRequestStatus(
          bookingRequestId,
          'manual_review',
          'system',
          `Low parser confidence: ${parsed.confidence}`
        );

        // Still process what we can, but mark for review
        const response: AvailabilityResponse = {
          available: parsed.available,
          confirmedRooms: parsed.confirmedRooms,
          totalPrice: parsed.totalPrice,
          currency: parsed.currency,
          confirmationNumber: parsed.confirmationNumber,
          checkInInstructions: parsed.checkInInstructions,
          responseDate: new Date().toISOString(),
          responseMethod: 'email',
          message: emailContent.substring(0, 500) // First 500 chars
        };

        return await this.processProviderResponse(bookingRequestId, response, 'email');
      }

      // High confidence - process automatically
      const response: AvailabilityResponse = {
        available: parsed.available,
        confirmedRooms: parsed.confirmedRooms,
        totalPrice: parsed.totalPrice,
        currency: parsed.currency,
        confirmationNumber: parsed.confirmationNumber,
        checkInInstructions: parsed.checkInInstructions,
        responseDate: new Date().toISOString(),
        responseMethod: 'email',
        message: parsed.parsedFields.message || emailContent.substring(0, 500)
      };

      return await this.processProviderResponse(bookingRequestId, response, 'email');
    } catch (error) {
      console.error('Error processing email response:', error);
      throw error;
    }
  }

  /**
   * Process provider response (email or API)
   */
  async processProviderResponse(
    bookingRequestId: string,
    response: AvailabilityResponse,
    responseMethod: 'email' | 'api' | 'phone'
  ): Promise<AccommodationBookingRequest | null> {
    try {
      const bookingRequest = await this.getBookingRequestById(bookingRequestId);
      if (!bookingRequest) {
        throw new Error('Booking request not found');
      }

      // Validate state transition
      if (bookingRequest.status !== 'sent' && bookingRequest.status !== 'awaiting_response') {
        throw new Error(`Cannot process response for booking in ${bookingRequest.status} status`);
      }

      // Update booking request with response
      const updated = await supabase
        .from('accommodation_booking_requests')
        .update({
          availability_response: response,
          last_communication_at: new Date().toISOString(),
          status: response.available
            ? (response.confirmedRooms && bookingRequest.requestedRooms && response.confirmedRooms < bookingRequest.requestedRooms
              ? 'partial_confirmed'
              : 'confirmed')
            : 'rejected',
          confirmation_number: response.confirmationNumber,
          confirmed_rooms: response.confirmedRooms,
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingRequestId)
        .select()
        .single();

      if (updated.error) {
        throw updated.error;
      }

      // Log communication
      await this.logCommunication({
        bookingRequestId,
        direction: 'inbound',
        method: responseMethod,
        recipient: bookingRequest.providerContactEmail || '',
        sender: bookingRequest.providerId, // Provider ID as sender identifier
        subject: 'Availability Response',
        content: response.message || JSON.stringify(response),
        status: 'delivered',
        timestamp: new Date().toISOString()
      });

      // Notify client if confirmed
      if (response.available) {
        await this.notifyClientOfConfirmation(bookingRequest, response);
      }

      return this.mapDbToBookingRequest(updated.data);
    } catch (error) {
      console.error('Error processing provider response:', error);
      throw error;
    }
  }

  /**
   * Notify client of confirmation (via their account, not direct email to provider)
   */
  private async notifyClientOfConfirmation(
    bookingRequest: AccommodationBookingRequest,
    response: AvailabilityResponse
  ): Promise<void> {
    // In production, send notification to client's account dashboard
    // or email them directly (NOT through provider)
    console.log('Notifying client of confirmation:', {
      clientEmail: bookingRequest.clientEmail,
      location: bookingRequest.location,
      confirmed: response.available
    });

    // Store notification in database for client portal
    // This would be displayed in the client's accommodation management interface
  }

  /**
   * Log all communications (for audit trail)
   */
  private async logCommunication(communication: Omit<AccommodationCommunication, 'id' | 'createdAt'>): Promise<void> {
    try {
      await supabase
        .from('accommodation_communications')
        .insert({
          booking_request_id: communication.bookingRequestId,
          direction: communication.direction,
          method: communication.method,
          recipient: communication.recipient,
          sender: communication.sender,
          subject: communication.subject,
          content: communication.content,
          status: communication.status,
          metadata: communication.metadata || {}
        });
    } catch (error) {
      console.error('Error logging communication:', error);
      // Don't throw - communication logging is non-critical
    }
  }

  /**
   * Get booking request by ID
   */
  async getBookingRequestById(id: string): Promise<AccommodationBookingRequest | null> {
    try {
      const { data, error } = await supabase
        .from('accommodation_booking_requests')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching booking request:', error);
        return null;
      }

      return this.mapDbToBookingRequest(data);
    } catch (error) {
      console.error('Error in getBookingRequestById:', error);
      return null;
    }
  }

  /**
   * Map database row to AccommodationBookingRequest
   */
  private mapDbToBookingRequest(dbRow: any): AccommodationBookingRequest {
    return {
      id: dbRow.id,
      safariBookingId: dbRow.safari_booking_id,
      clientId: dbRow.client_id,
      providerId: dbRow.provider_id,
      tourId: dbRow.tour_id,
      location: dbRow.location,
      clientName: dbRow.client_name,
      clientEmail: dbRow.client_email,
      clientPhone: dbRow.client_phone,
      clientNationality: dbRow.client_nationality,
      clientSpecialRequests: dbRow.client_special_requests,
      providerContactName: dbRow.provider_contact_name,
      providerContactEmail: dbRow.provider_contact_email,
      providerContactPhone: dbRow.provider_contact_phone,
      groupName: dbRow.group_name,
      groupSize: dbRow.group_size || dbRow.guest_count || 1,
      guestCount: dbRow.guest_count,
      checkInDate: dbRow.check_in_date || dbRow.arrival_date,
      checkOutDate: dbRow.check_out_date || dbRow.departure_date,
      arrivalDate: dbRow.arrival_date,
      departureDate: dbRow.departure_date,
      nights: dbRow.nights,
      roomRequirements: dbRow.room_requirements || dbRow.room_type || 'standard',
      roomType: dbRow.room_type,
      mealPlan: dbRow.meal_plan,
      status: dbRow.status as BookingRequestStatus,
      priority: dbRow.priority || 'medium',
      responseDeadline: dbRow.response_deadline,
      availabilityResponse: dbRow.availability_response,
      confirmationNumber: dbRow.confirmation_number,
      requestedRooms: dbRow.requested_rooms,
      confirmedRooms: dbRow.confirmed_rooms,
      requestedNights: dbRow.requested_nights,
      confirmedNights: dbRow.confirmed_nights,
      partialConfirmationDetails: dbRow.partial_confirmation_details,
      idempotencyKey: dbRow.idempotency_key,
      version: dbRow.version,
      lockUntil: dbRow.lock_until,
      lockedBy: dbRow.locked_by,
      paymentAuthorized: dbRow.payment_authorized,
      paymentCaptured: dbRow.payment_captured,
      paymentAuthorizationId: dbRow.payment_authorization_id,
      paymentAmount: dbRow.payment_amount ? parseFloat(dbRow.payment_amount) : undefined,
      paymentCurrency: dbRow.payment_currency,
      lastCommunicationAt: dbRow.last_communication_at,
      createdAt: dbRow.created_at,
      updatedAt: dbRow.updated_at
    };
  }

  /**
   * Create booking requests for all accommodation selections in a custom safari
   */
  async createBookingRequestsForSafari(
    safariBookingId: string,
    clientId: string,
    itinerary: CustomSafariItinerary,
    accommodationSelections: Array<{
      tourId: string;
      location: string;
      providerId?: string;
      nights: number;
      roomType?: string;
      mealPlan?: 'breakfast' | 'halfBoard' | 'fullBoard' | 'none';
    }>,
    clientDetails: {
      name: string;
      email: string;
      phone: string;
      nationality?: string;
      specialRequests?: string;
    }
  ): Promise<AccommodationBookingRequest[]> {
    const bookingRequests: AccommodationBookingRequest[] = [];

    for (const selection of accommodationSelections) {
      if (!selection.providerId) {
        // "Let SightTrails choose" - skip for now, admin will handle
        continue;
      }

      // Find the tour to get dates
      const tour = itinerary.tours.find(t => t.tour.id === selection.tourId);
      if (!tour) {
        continue;
      }

      // Calculate dates based on tour dates and nights
      const arrivalDate = tour.startDate || new Date().toISOString().split('T')[0];
      const departureDate = new Date(new Date(arrivalDate).getTime() + selection.nights * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];

      try {
        const bookingRequest = await this.createBookingRequest(
          safariBookingId,
          clientId,
          selection.providerId,
          selection.tourId,
          selection.location,
          clientDetails,
          {
            guestCount: itinerary.tours.reduce((sum, t) => sum + t.participants, 0),
            arrivalDate,
            departureDate,
            nights: selection.nights,
            roomType: selection.roomType,
            mealPlan: selection.mealPlan
          }
        );

        if (bookingRequest) {
          bookingRequests.push(bookingRequest);
        }
      } catch (error) {
        console.error(`Error creating booking request for ${selection.location}:`, error);
        // Continue with other requests
      }
    }

    return bookingRequests;
  }
}

export const bookingRequestService = new BookingRequestService();

